import FkPicker from "../../components/FkPicker";

const INPUT_BASE = "w-full px-3 py-2 text-sm border border-[#E6E1D6] rounded outline-none focus:border-[#0F1829] bg-white";

export function inputTypeFor(col) {
  const t = (col.DATA_TYPE || "").toLowerCase();
  if (["int", "bigint", "smallint", "tinyint"].includes(t)) return "number";
  if (["decimal", "numeric", "float", "real", "money"].includes(t)) return "number";
  if (["date"].includes(t)) return "date";
  if (["datetime", "datetime2", "smalldatetime"].includes(t)) return "datetime-local";
  if (["bit"].includes(t)) return "checkbox";
  return "text";
}

// Columns longer than 255 chars (or NVARCHAR(MAX)) render as textarea.
function isLongText(col) {
  if (col.DATA_TYPE === "text" || col.DATA_TYPE === "ntext") return true;
  const n = col.CHARACTER_MAXIMUM_LENGTH;
  return !!n && (n === -1 || n > 255);
}

function YesNoSelect({ col, value, onChange }) {
  return (
    <select value={value ?? ""} onChange={(e) => onChange(e.target.value)}
      className={INPUT_BASE} data-testid={`field-${col.COLUMN_NAME}`}>
      <option value="">—</option>
      <option value="S">S (Sim)</option>
      <option value="N">N (Não)</option>
      <option value="1">1 (true)</option>
      <option value="0">0 (false)</option>
    </select>
  );
}

function LongTextArea({ col, value, onChange }) {
  return (
    <textarea value={value ?? ""} onChange={(e) => onChange(e.target.value)}
      rows={4} className={INPUT_BASE + " font-mono"} data-testid={`field-${col.COLUMN_NAME}`} />
  );
}

function RegularInput({ col, value, onChange, type }) {
  return (
    <input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)}
      maxLength={col.CHARACTER_MAXIMUM_LENGTH > 0 ? col.CHARACTER_MAXIMUM_LENGTH : undefined}
      className={INPUT_BASE} data-testid={`field-${col.COLUMN_NAME}`} />
  );
}

/**
 * Dispatches to the correct editor based on the column metadata:
 * - `col.FK` + context → FkPicker (relational record picker)
 * - `bit` type → Yes/No select
 * - long text → textarea
 * - everything else → typed input (number/date/datetime-local/text)
 */
export function renderField(col, value, onChange, fkCtx) {
  if (col.FK && fkCtx) {
    return (
      <FkPicker
        schema={fkCtx.schema} name={fkCtx.name} column={col.COLUMN_NAME}
        value={value} onChange={onChange}
      />
    );
  }
  const type = inputTypeFor(col);
  if (type === "checkbox") return <YesNoSelect col={col} value={value} onChange={onChange} />;
  if (isLongText(col)) return <LongTextArea col={col} value={value} onChange={onChange} />;
  return <RegularInput col={col} value={value} onChange={onChange} type={type} />;
}

/**
 * Read-only rendering of a field (used when the page is in "view" mode, or for
 * PK columns / identity columns on edit). FK columns still render a disabled
 * FkPicker so the UI can resolve the label instead of just showing an id.
 */
export function ReadonlyField({ col, value, schema, name, isCreate }) {
  if (col.FK && !isCreate) {
    return <FkPicker schema={schema} name={name} column={col.COLUMN_NAME} value={value} onChange={() => {}} disabled />;
  }
  return (
    <input disabled value={value ?? ""} placeholder={col.IsIdentity ? "(auto)" : ""}
      className="w-full px-3 py-2 text-sm border border-[#E6E1D6] rounded bg-[#F5F2EB] text-[#6B7280] font-mono" />
  );
}

// ── Save-time transforms ────────────────────────────────────────────────

/** Normalize datetime-local values ("YYYY-MM-DDTHH:MM") → "YYYY-MM-DD HH:MM" for SQL Server. */
function normalizeDatetimeFields(values, columns) {
  const out = { ...values };
  for (const c of columns) {
    if (!(c.COLUMN_NAME in out)) continue;
    const v = out[c.COLUMN_NAME];
    if (inputTypeFor(c) === "datetime-local" && typeof v === "string" && v.includes("T")) {
      out[c.COLUMN_NAME] = v.replace("T", " ");
    }
  }
  return out;
}

/** Build the POST payload for a new record: drop identity columns + normalize datetimes. */
export function buildCreatePayload(values, columns) {
  const payload = { ...values };
  for (const c of columns) {
    if (c.IsIdentity) delete payload[c.COLUMN_NAME];
  }
  return normalizeDatetimeFields(payload, columns);
}

/** Compare values vs initial, returning only the changed fields (normalized for datetime). */
export function buildEditDiff(values, initialValues, columns) {
  const diff = {};
  for (const [k, v] of Object.entries(values)) {
    const before = initialValues[k];
    const beforeNorm = before === null || before === undefined ? "" : before;
    const afterNorm = v === null || v === undefined ? "" : v;
    if (beforeNorm === afterNorm) continue;
    // Treat numeric equality (API may return 5 while input holds "5") as no-change.
    if (typeof beforeNorm === "number" && String(beforeNorm) === String(afterNorm)) continue;
    diff[k] = v;
  }
  return normalizeDatetimeFields(diff, columns);
}
